/**
 * Enter 手动登录会话号。
 *
 * 交互式登录会生成新的会话号。当前账号已经登录但还没有会话号时补一个，
 * 这样功能上线前就保持登录的用户不用先退出。刷新、重开页面和静默登录沿用已有值；
 * 退出登录时清除。它只用于营销弹窗展示，不是认证凭证。
 */
const LOGIN_SESSION_QUERY_PARAM = 'enter_login';
const LOGIN_SESSION_STORAGE_KEY = 'enter_login_session';
const PENDING_LOGIN_SESSION_STORAGE_KEY = 'enter_login_session_pending';

interface StoredLoginSession {
  sub: string;
  id: string;
}

// 存储不可用（隐私模式、配额已满）时静默跳过：结果只是不展示 every_login 弹窗，不影响登录。
function readItem(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeItem(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // 见 readItem。
  }
}

function removeItem(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // 见 readItem。
  }
}

/** 交互式登录跳转前调用：生成本次登录会话号，记为待确认，并写进登录完成后的回跳地址。 */
export function appendLoginSessionMarker(path: string, origin: string): string {
  const loginSessionId = crypto.randomUUID();
  writeItem(PENDING_LOGIN_SESSION_STORAGE_KEY, loginSessionId);
  const url = new URL(path, origin);
  url.searchParams.set(LOGIN_SESSION_QUERY_PARAM, loginSessionId);
  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * 确认已登录后调用。地址栏中的会话号与待确认的一致才生效，
 * 旧链接、后退、刷新带回的参数，以及之后静默登录都不会被当成一次新登录。
 * 参数无论是否生效都会从地址栏去掉。
 */
export function completeLoginSession(sub: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  const url = new URL(window.location.href);
  const loginSessionId = url.searchParams.get(LOGIN_SESSION_QUERY_PARAM);
  if (loginSessionId === null) {
    return;
  }

  if (loginSessionId && loginSessionId === readItem(PENDING_LOGIN_SESSION_STORAGE_KEY)) {
    const session: StoredLoginSession = { sub, id: loginSessionId };
    writeItem(LOGIN_SESSION_STORAGE_KEY, JSON.stringify(session));
    removeItem(PENDING_LOGIN_SESSION_STORAGE_KEY);
  }

  url.searchParams.delete(LOGIN_SESSION_QUERY_PARAM);
  window.history.replaceState(
    window.history.state,
    '',
    `${url.pathname}${url.search}${url.hash}`
  );
}

/**
 * 当前账号没有会话号时补一个并保存。已有会话号原样返回，刷新和静默登录不会更换。
 * 存储不可用时返回 undefined，不影响登录。
 */
export function ensureLoginSession(sub: string): string | undefined {
  const existing = readLoginSessionId(sub);
  if (existing) {
    return existing;
  }
  if (typeof window === 'undefined' || !sub) {
    return undefined;
  }

  const session: StoredLoginSession = { sub, id: crypto.randomUUID() };
  writeItem(LOGIN_SESSION_STORAGE_KEY, JSON.stringify(session));
  return readLoginSessionId(sub);
}

/** 只返回当前账号自己的会话号，换账号不会继承。 */
export function readLoginSessionId(sub: string): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const raw = readItem(LOGIN_SESSION_STORAGE_KEY);
  if (!raw) {
    return undefined;
  }

  try {
    const session = JSON.parse(raw) as Partial<StoredLoginSession> | null;
    return session?.sub === sub && typeof session.id === 'string' && session.id
      ? session.id
      : undefined;
  } catch {
    return undefined;
  }
}

export function clearLoginSession(): void {
  if (typeof window === 'undefined') {
    return;
  }

  removeItem(LOGIN_SESSION_STORAGE_KEY);
  removeItem(PENDING_LOGIN_SESSION_STORAGE_KEY);
}
